import React from "react"
import {
	Grid,
	Typography,
	Avatar,
	IconButton,
	Card,
	Button as MuiButton
} from "@material-ui/core"
import {
	Delete as DeleteIcon,
	Edit as EditIcon,
	CalendarToday as CalendarTodayIcon
} from "@material-ui/icons"

import { Divider } from "@/components"
import { MeetingAnalysis } from "@/services/MeetingAnalyzerService"
import { formatDateInBrazilianDate, getDateWithBrazilianTimezone } from "@/utils/time"

import useMeetingAnalyzerStyles from "@/pages/Admin/MeetingAnalyzer/Detail/styles"
import useHeaderStyles from "@/pages/Admin/MeetingAnalyzer/Detail/Header/styles"

const NO_DATE_LABEL = "não identificada"
const ANALYZED_AT_LABEL = "Analisada em"
const NO_LEAD_LABEL = "—"

const getInitials = (name: string): string => {
	const words = name.trim().split(/\s+/)

	if (words.length === 0) {
		return "?"
	}

	if (words.length === 1) {
		return words[0].charAt(0).toUpperCase()
	}

	const firstInitial = words[0].charAt(0).toUpperCase()
	const lastInitial = words[words.length - 1].charAt(0).toUpperCase()

	return `${firstInitial}${lastInitial}`
}

const formatMeetingDate = (meetingDate: string | null): string => {
	if (meetingDate === null) {
		return NO_DATE_LABEL
	}

	return formatDateInBrazilianDate(getDateWithBrazilianTimezone(meetingDate))
}

type HeaderProps = {
	analysis: MeetingAnalysis
	onDelete: () => void
	onOpenDateEdit: (anchorElement: HTMLButtonElement) => void
}

const Header: React.FC<HeaderProps> = (props) => {
	const {
		analysis,
		onDelete,
		onOpenDateEdit
	} = props

	const meetingAnalyzerClasses = useMeetingAnalyzerStyles()
	const classes = useHeaderStyles()

	const renderDateAndDurationBlock = () => {
		return (
			<Grid container direction="column" spacing={1}>
				<Grid item>
					<Grid container alignItems="center" spacing={1}>
						<Grid item>
							<CalendarTodayIcon fontSize="small" />
						</Grid>
						<Grid item>
							<Typography className={meetingAnalyzerClasses.metaLabel}>
								Data da reunião: {formatMeetingDate(analysis.output?.meetingDate ?? null)}
							</Typography>
						</Grid>
						<Grid item>
							<IconButton
								size="small"
								onClick={(event) => onOpenDateEdit(event.currentTarget)}
							>
								<EditIcon fontSize="small" />
							</IconButton>
						</Grid>
					</Grid>
				</Grid>
				<Grid item>
					<Typography className={classes.dateSecondary}>
						{ANALYZED_AT_LABEL} {formatDateInBrazilianDate(new Date(analysis.createdAt))}
					</Typography>
				</Grid>
			</Grid>
		)
	}

	return (
		<Grid container direction="column" spacing={0}>
			<Grid item xs={12}>
				<Grid container justifyContent="space-between" alignItems="flex-start">
					<Grid item xs={6}>
						<Typography variant="h3" className={classes.detailTitle}>
							Análise da Reunião com “{analysis.output?.participants?.leadName ?? NO_LEAD_LABEL}”
						</Typography>
					</Grid>
					<Grid item>
						<Grid container direction="row" spacing={2}>
							<Grid item>
								<MuiButton
									variant="outlined"
									className={classes.deleteAnalysisButton}
									onClick={onDelete}
									style={{ height: "50px" }}
								>
									<DeleteIcon fontSize="medium" />
									<Divider orientation="vertical" size={1} />
									EXCLUIR ANÁLISE
								</MuiButton>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12}>
				<Card className={meetingAnalyzerClasses.card}>
					<Grid container justifyContent="space-between" alignItems="center" spacing={3}>
						<Grid item>
							<Grid container alignItems="center" spacing={2}>
								<Grid item>
									<Avatar className={classes.closerAvatar}>
										{getInitials(analysis.userName)}
									</Avatar>
								</Grid>
								<Grid item>
									<Grid container direction="column">
										<Grid item>
											<Typography className={classes.eyebrowLabel}>
												CLOSER
											</Typography>
										</Grid>
										<Grid item>
											<Typography className={classes.summaryHeading}>
												{analysis.userName}
											</Typography>
										</Grid>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
						<Grid item>
							<Grid container direction="column">
								<Grid item>
									<Typography className={classes.eyebrowLabel}>
										LEAD
									</Typography>
								</Grid>
								<Grid item>
									<Typography className={classes.summaryHeading}>
										{analysis.output?.participants?.leadName ?? NO_LEAD_LABEL} · {analysis.output?.participants?.leadCompany ?? NO_LEAD_LABEL}
									</Typography>
								</Grid>
							</Grid>
						</Grid>
						<Grid item>
							{renderDateAndDurationBlock()}
						</Grid>
					</Grid>
				</Card>
			</Grid>
		</Grid>
	)
}

export default Header
